import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/apiClient';
import PageHeader from '../components/PageHeader';
import useAuth from '../hooks/useAuth';

// Helper function to format Cloudflare R2 / Upload image URLs
const getFullImageUrl = (img) => {
  if (!img) return null;
  const str = String(img).trim();
  if (!str || ['null', 'undefined', 'none', 'false', '0'].includes(str.toLowerCase())) return null;
  if (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('blob:')) return str;
  const clean = str.replace(/^\/+/, '').replace(/^uploads\//, '');
  return `https://pub-8403cb60474c4e039483cf5333503261.r2.dev/${clean}`;
};

// Helper for Category SVG Icons matching client reference image
const getCategorySvgIcon = (keyName) => {
  const norm = String(keyName || '').toLowerCase();

  // Fine Motor
  if (norm.includes('fine') || norm.includes('finger') || norm.includes('hand')) {
    return (
      <div className="mj-area-icon-wrap" style={{ background: '#fbcfe8', color: '#db2777' }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 11V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v0"/>
          <path d="M14 10V4a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v6"/>
          <path d="M10 10.5V6a2 2 0 0 0-2-2v0a2 2 0 0 0-2 2v8"/>
          <path d="M18 8a2 2 0 0 1 2 2v4a8 8 0 0 1-8 8h-2c-2.5 0-4.5-1.5-5.5-3.5L2.5 13"/>
        </svg>
      </div>
    );
  }

  // Gross Motor
  if (norm.includes('gross') || norm.includes('motor') || norm.includes('physical') || norm.includes('walk')) {
    return (
      <div className="mj-area-icon-wrap" style={{ background: '#dcfce7', color: '#16a34a' }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="13" cy="4" r="2"/>
          <path d="m11 19-3 3"/>
          <path d="m14 14 2 5"/>
          <path d="m9 11 4 3 3-3"/>
          <path d="m6 13 3-2 3 3"/>
        </svg>
      </div>
    );
  }

  // Cognitive
  if (norm.includes('cognit') || norm.includes('puzzle') || norm.includes('problem')) {
    return (
      <div className="mj-area-icon-wrap" style={{ background: '#ffedd5', color: '#ea580c' }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19.439 7.85c-.049-.322.059-.648.289-.878l1.568-1.568a2.121 2.121 0 0 0-3-3l-1.568 1.568c-.23.23-.556.338-.878.289A4.003 4.003 0 0 0 11.45 6.45L6.45 11.45a4.003 4.003 0 0 0 2.189 4.397c.322.049.648-.059.878-.289l1.568-1.568a2.121 2.121 0 0 1 3 3l-1.568 1.568c-.23.23-.338.556-.289.878A4.003 4.003 0 0 0 16.55 21.45l5-5a4.003 4.003 0 0 0-2.111-8.6z"/>
        </svg>
      </div>
    );
  }

  // Language
  if (norm.includes('lang') || norm.includes('speech') || norm.includes('literacy')) {
    return (
      <div className="mj-area-icon-wrap" style={{ background: '#dbeafe', color: '#2563eb' }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          <line x1="8" y1="9" x2="16" y2="9"/>
          <line x1="8" y1="13" x2="14" y2="13"/>
        </svg>
      </div>
    );
  }

  // Montessori / Practical Life
  if (norm.includes('montessor') || norm.includes('material') || norm.includes('practical')) {
    return (
      <div className="mj-area-icon-wrap" style={{ background: '#f3e8ff', color: '#9333ea' }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="7" rx="1"/>
          <rect x="14" y="3" width="7" height="7" rx="1"/>
          <rect x="14" y="14" width="7" height="7" rx="1"/>
          <rect x="3" y="14" width="7" height="7" rx="1"/>
        </svg>
      </div>
    );
  }

  // Sensorial
  if (norm.includes('sensor') || norm.includes('touch') || norm.includes('visual')) {
    return (
      <div className="mj-area-icon-wrap" style={{ background: '#ccfbf1', color: '#0d9488' }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
          <circle cx="12" cy="12" r="3"/>
        </svg>
      </div>
    );
  }

  // Mathematical
  if (norm.includes('math') || norm.includes('number') || norm.includes('count') || norm.includes('logic')) {
    return (
      <div className="mj-area-icon-wrap" style={{ background: '#e0e7ff', color: '#4338ca' }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="4" y="2" width="16" height="20" rx="2"/>
          <line x1="8" y1="6" x2="16" y2="6"/>
          <line x1="16" y1="14" x2="16" y2="18"/>
          <path d="M16 10h.01"/><path d="M12 10h.01"/><path d="M8 10h.01"/><path d="M12 14h.01"/><path d="M8 14h.01"/><path d="M12 18h.01"/><path d="M8 18h.01"/>
        </svg>
      </div>
    );
  }

  // History / Society / Culture
  if (norm.includes('histor') || norm.includes('societ') || norm.includes('cultur') || norm.includes('world')) {
    return (
      <div className="mj-area-icon-wrap" style={{ background: '#fef3c7', color: '#d97706' }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <line x1="2" y1="12" x2="22" y2="12"/>
          <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
        </svg>
      </div>
    );
  }

  // Knowledge of Personal Details / Identity
  if (norm.includes('person') || norm.includes('detail') || norm.includes('identit') || norm.includes('self')) {
    return (
      <div className="mj-area-icon-wrap" style={{ background: '#cffafe', color: '#0891b2' }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
          <circle cx="12" cy="7" r="4"/>
        </svg>
      </div>
    );
  }

  // Concentration and attention span
  if (norm.includes('concentrat') || norm.includes('attent') || norm.includes('focus') || norm.includes('mind')) {
    return (
      <div className="mj-area-icon-wrap" style={{ background: '#fae8ff', color: '#c026d3' }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"/>
          <path d="M9 18h6"/>
          <path d="M10 22h4"/>
        </svg>
      </div>
    );
  }

  // School Readiness
  if (norm.includes('school') || norm.includes('readiness') || norm.includes('graduat') || norm.includes('academ')) {
    return (
      <div className="mj-area-icon-wrap" style={{ background: '#d1fae5', color: '#059669' }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
          <path d="M6 12v5c3 3 9 3 12 0v-5"/>
        </svg>
      </div>
    );
  }

  // Geography / Science / Nature Awareness
  if (norm.includes('geograph') || norm.includes('scienc') || norm.includes('natur') || norm.includes('environment')) {
    return (
      <div className="mj-area-icon-wrap" style={{ background: '#ccfbf1', color: '#0d9488' }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.4 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z"/>
          <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12"/>
        </svg>
      </div>
    );
  }

  // Social & Emotional (Grace and Courtesy)
  if (norm.includes('social') || norm.includes('emot') || norm.includes('grace') || norm.includes('courtes')) {
    return (
      <div className="mj-area-icon-wrap" style={{ background: '#ffe4e6', color: '#e11d48' }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>
        </svg>
      </div>
    );
  }

  // Default Colorful Star Badge
  return (
    <div className="mj-area-icon-wrap" style={{ background: '#f3e8ff', color: '#7c3aed' }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
      </svg>
    </div>
  );
};

// Helper function to format raw keys cleanly (e.g. montessori_materials -> Montessori Materials)
const formatKeyText = (text) => {
  if (!text) return '';
  let str = String(text).trim();
  
  // Known custom mappings for standard terms
  const customMap = {
    'montessori_materials': 'Montessori Materials',
    'not_yet_presented': 'Not Yet Presented',
    'presented_and_mastered': 'Achieved / Mastered',
    'presented_and_progressing': 'Developing',
    'support_required': 'Support Required',
    'not_yet_observed': 'Not Yet Observed',
    'achieved': 'Achieved',
    'mastered': 'Mastered',
    'developing': 'Developing'
  };

  const lower = str.toLowerCase();
  if (customMap[lower]) return customMap[lower];

  // Replace underscores and camelCase
  str = str.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\s+/g, ' ');
  return str.replace(/\b\w/g, c => c.toUpperCase());
};

// Helper for status badge styling & standard display label
const getBadgeDetails = (statusRaw) => {
  if (!statusRaw) return { text: 'NOT YET PRESENTED', className: 'badge-not-yet-presented' };
  
  const st = String(statusRaw).trim().toLowerCase();
  
  if (st.includes('achieved') || st.includes('mastered')) {
    return { text: 'ACHIEVED', className: 'badge-achieved' };
  }
  if (st.includes('developing') || st.includes('progressing')) {
    return { text: 'DEVELOPING', className: 'badge-developing' };
  }
  if (st.includes('support') || st.includes('required') || st.includes('focus') || st.includes('emerging')) {
    return { text: 'SUPPORT REQUIRED', className: 'badge-support' };
  }
  if (st.includes('not') || st.includes('yet') || st.includes('observed') || st.includes('presented')) {
    return { text: 'NOT YET PRESENTED', className: 'badge-not-yet-presented' };
  }
  
  return { text: formatKeyText(statusRaw).toUpperCase(), className: 'badge-developing' };
};

const isStandardStatusValue = (val) => {
  if (!val) return true;
  const s = String(val).trim().toLowerCase();
  return (
    s === 'achieved' ||
    s === 'mastered' ||
    s === 'developing' ||
    s === 'progressing' ||
    s === 'support required' ||
    s === 'support_required' ||
    s === 'support' ||
    s === 'required' ||
    s === 'emerging' ||
    s === 'not yet presented' ||
    s === 'not_yet_presented' ||
    s === 'not yet' ||
    s === 'not observed' ||
    s === 'not_observed' ||
    s === 'code 1' || s === 'code 2' || s === 'code 3' || s === 'code 4'
  );
};

// Helper for CAPS rating details (Code 4 to Code 1)
const getCapsRatingDetails = (statusRaw) => {
  if (!statusRaw) return { code: '1', level: 'Code 1', text: 'Support Required (1%-34%)', color: '#991b1b', bg: '#fee2e2' };
  const st = String(statusRaw).toLowerCase();
  if (st.includes('achieved') || st.includes('mastered')) {
    return { code: '4', level: 'Code 4', text: 'Outstanding Achievement (75%-100%)', color: '#065f46', bg: '#d1fae5' };
  }
  if (st.includes('developing') || st.includes('progressing')) {
    return { code: '3', level: 'Code 3', text: 'Substantial Achievement (50%-74%)', color: '#1e40af', bg: '#dbeafe' };
  }
  if (st.includes('support') || st.includes('required') || st.includes('emerging')) {
    return { code: '2', level: 'Code 2', text: 'Moderate Achievement (35%-49%)', color: '#92400e', bg: '#fef3c7' };
  }
  return { code: '1', level: 'Code 1', text: 'Support Required (1%-34%)', color: '#475569', bg: '#f1f5f9' };
};

// Helper for Montessori observation codes (M, P, I, F)
const getMontessoriDetails = (statusRaw) => {
  if (!statusRaw) return { code: 'F', text: 'FUTURE FOCUS', color: '#475569', bg: '#f1f5f9' };
  const st = String(statusRaw).toLowerCase();
  if (st.includes('achieved') || st.includes('mastered')) {
    return { code: 'M', text: 'MASTERED', color: '#047857', bg: '#ecfdf5' };
  }
  if (st.includes('developing') || st.includes('progressing') || st.includes('practicing')) {
    return { code: 'P', text: 'PRACTICING', color: '#0284c7', bg: '#f0f9ff' };
  }
  if (st.includes('support') || st.includes('introduced')) {
    return { code: 'I', text: 'INTRODUCED', color: '#b45309', bg: '#fffbeb' };
  }
  return { code: 'F', text: 'FUTURE FOCUS', color: '#475569', bg: '#f8fafc' };
};

export default function ReportDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const fromParam = searchParams.get('from');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSections, setSelectedSections] = useState(['All']);
  const [reportFormat, setReportFormat] = useState('milestone_journey'); // 'milestone_journey' | 'ncf' | 'caps' | 'montessori'
  const [journeyData, setJourneyData] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const goBack = () => {
    if (fromParam === 'student' && report) {
      navigate(`/students/${report.student_id}`);
    } else {
      navigate('/reports');
    }
  };

  useEffect(() => {
    fetchReport();
  }, [id]);

  useEffect(() => {
    const handleBeforePrint = () => {
      document.title = '\u200B';
    };
    const handleAfterPrint = () => {
      document.title = 'Lotus Admin Panel';
    };

    window.addEventListener('beforeprint', handleBeforePrint);
    window.addEventListener('afterprint', handleAfterPrint);

    return () => {
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
      document.title = 'Lotus Admin Panel';
    };
  }, []);

  const [publishing, setPublishing] = useState(false);

  const handleStatusChange = async (newStatus) => {
    setPublishing(true);
    try {
      if (newStatus === 'published') {
        await api.put(`/admin/student-reports/${report.id}/publish`);
      } else {
        await api.put(`/admin/student-reports/${report.id}`, { ...report, status: 'draft' });
      }
      await fetchReport();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update report status');
    } finally {
      setPublishing(false);
    }
  };

  const handlePrint = () => {
    document.title = '\u200B';
    window.print();
    setTimeout(() => {
      document.title = 'Lotus Admin Panel';
    }, 500);
  };

  async function fetchReport() {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/admin/student-reports/${id}`);
      setReport(response.data.data);
      try {
        const jRes = await api.get(`/admin/student-reports/${id}/milestone-journey`);
        setJourneyData(jRes.data.data);
      } catch (jErr) {
        console.error('Milestone journey fetch error:', jErr);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to load report details');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return <div style={{ padding: 24 }}>Loading report details...</div>;
  }

  if (error || !report) {
    return (
      <div style={{ padding: 24 }}>
        <div className="error-message">{error || 'Report not found.'}</div>
        <button type="button" className="secondary-button" onClick={() => navigate('/reports')}>
          Back to Reports
        </button>
      </div>
    );
  }

  const assessments = report.assessments || {};
  const sections = Object.keys(assessments);

  // Format switcher & options UI bar
  const isAllSelected = selectedSections.includes('All');

  // Compute Overall Progress Snapshot metrics dynamically
  let totalMilestones = 0;
  let achievedCount = 0;
  let developingCount = 0;
  let supportCount = 0;
  let notYetCount = 0;

  Object.values(assessments).forEach((secItems) => {
    if (secItems && typeof secItems === 'object') {
      Object.values(secItems).forEach((stRaw) => {
        totalMilestones++;
        const st = String(stRaw).toLowerCase();
        if (st.includes('achieved') || st.includes('mastered')) achievedCount++;
        else if (st.includes('developing') || st.includes('progressing')) developingCount++;
        else if (st.includes('support') || st.includes('required') || st.includes('emerging')) supportCount++;
        else notYetCount++;
      });
    }
  });

  const achievedPct = totalMilestones > 0 ? ((achievedCount / totalMilestones) * 100).toFixed(1) : 0;
  const developingPct = totalMilestones > 0 ? ((developingCount / totalMilestones) * 100).toFixed(1) : 0;
  const supportPct = totalMilestones > 0 ? ((supportCount / totalMilestones) * 100).toFixed(1) : 0;
  const notYetPct = totalMilestones > 0 ? (((totalMilestones - achievedCount - developingCount - supportCount) / totalMilestones) * 100).toFixed(1) : 0;

  const toggleSection = (secKey) => {
    if (secKey === 'All') {
      setSelectedSections(['All']);
      return;
    }

    let updated;
    if (selectedSections.includes('All')) {
      updated = [secKey];
    } else if (selectedSections.includes(secKey)) {
      updated = selectedSections.filter(s => s !== secKey);
      if (updated.length === 0) updated = ['All'];
    } else {
      updated = [...selectedSections, secKey];
      if (sections.length > 1 && updated.length === sections.length) {
        updated = ['All'];
      }
    }
    setSelectedSections(updated);
  };

  const isSectionCheckedInUI = (secKey) => {
    if (secKey === 'All') return isAllSelected;
    return !isAllSelected && selectedSections.includes(secKey);
  };

  const isSectionVisibleInReport = (secKey) => {
    return isAllSelected || selectedSections.includes(secKey);
  };

  return (
    <div>
      <div className="no-print">
        <PageHeader
          title="Student Development Report"
          subtitle={`Detailed milestone tracking and teacher assessments for ${report.student_name}.`}
        />

        <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center', flexWrap: 'wrap' }}>
          <button type="button" className="secondary-button" onClick={goBack}>
            {fromParam === 'student' ? '← Back to Student' : '← Back to Reports'}
          </button>

          <button type="button" className="primary-button" onClick={handlePrint}>
            🖨️ Print / Save PDF Report
          </button>

          <button
            type="button"
            style={{
              padding: '8px 16px',
              borderRadius: 8,
              background: '#eff6ff',
              color: '#1d4ed8',
              border: '1.5px solid #93c5fd',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
            onClick={() => navigate(`/reports?edit=${report.id}`)}
          >
            ✏️ Edit Report
          </button>

          {report.status === 'draft' ? (
            <button
              type="button"
              disabled={publishing}
              style={{
                padding: '8px 16px',
                borderRadius: 8,
                background: '#d1fae5',
                color: '#065f46',
                border: '1.5px solid #6ee7b7',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
              onClick={() => handleStatusChange('published')}
            >
              📢 {publishing ? 'Publishing...' : 'Publish Report'}
            </button>
          ) : (
            <button
              type="button"
              disabled={publishing}
              style={{
                padding: '8px 16px',
                borderRadius: 8,
                background: '#fef3c7',
                color: '#92400e',
                border: '1.5px solid #fcd34d',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
              onClick={() => handleStatusChange('draft')}
            >
              📝 {publishing ? 'Updating...' : 'Revert to Draft'}
            </button>
          )}

          <span style={{
            padding: '6px 12px',
            borderRadius: 16,
            fontSize: 12,
            fontWeight: 700,
            textTransform: 'uppercase',
            background: report.status === 'published' ? '#d1fae5' : '#fef3c7',
            color: report.status === 'published' ? '#065f46' : '#92400e',
            border: report.status === 'published' ? '1px solid #a7f3d0' : '1px solid #fde68a'
          }}>
            Status: {report.status || 'draft'}
          </span>

          {(user?.role === 'admin' || user?.role === 'center_manager') && (
            <button
              type="button"
              style={{ padding: '8px 16px', borderRadius: 8, background: '#fee2e2', color: '#991b1b', border: 'none', fontWeight: 600, cursor: 'pointer', marginLeft: 'auto' }}
              onClick={() => setShowDeleteModal(true)}
            >
              🗑️ Delete Report
            </button>
          )}
        </div>



        {showDeleteModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 400, boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
              <h3 style={{ marginTop: 0, color: '#dc2626' }}>Delete Report Card?</h3>
              <p style={{ color: '#475569', fontSize: 14 }}>
                Are you sure you want to delete report <strong>#{report.id}</strong> for student <strong>{report.student_name}</strong>?
                This action cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    setDeleting(true);
                    try {
                      await api.delete(`/admin/student-reports/${report.id}`);
                      navigate('/reports');
                    } catch (err) {
                      setError(err.response?.data?.message || 'Failed to delete report');
                    } finally {
                      setDeleting(false);
                      setShowDeleteModal(false);
                    }
                  }}
                  disabled={deleting}
                  style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', fontWeight: 600, cursor: 'pointer' }}
                >
                  {deleting ? 'Deleting...' : 'Yes, Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* PDF Selection Checkboxes (No Print) */}
        <div className="pdf-selection-box">
          <div className="pdf-selection-header">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2.5">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
            </svg>
            <span>Select PDF Sections to Include in Print:</span>
          </div>

          <div className="pdf-options-list">
            <label
              className={`pdf-option-card ${isAllSelected ? 'selected' : ''}`}
              onClick={() => toggleSection('All')}
            >
              <div className="custom-checkbox">
                {isAllSelected && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                )}
              </div>
              <span>All Sections (Full Report Card)</span>
              <span className="badge-full-pdf">FULL REPORT</span>
            </label>

            {sections.map((sec) => {
              const selected = isSectionCheckedInUI(sec);
              const formattedTitle = formatKeyText(sec);
              return (
                <label
                  key={sec}
                  className={`pdf-option-card ${selected ? 'selected' : ''}`}
                  onClick={() => toggleSection(sec)}
                >
                  <div className="custom-checkbox">
                    {selected && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    )}
                  </div>
                  <span>{formattedTitle}</span>
                </label>
              );
            })}
          </div>

          <div className="pdf-helper-text">
            <span>ℹ️</span>
            <span>
              {isAllSelected
                ? 'Printing complete report containing all developmental domains, educator notes & sign-off fields.'
                : `Filter active: Printing selected domain(s): ${selectedSections.map(formatKeyText).join(', ')}.`}
            </span>
          </div>
        </div>
      </div>

      {/* Styled Printable Container */}
      <div id="printable-report" className="report-card-container">
        {/* Style injection for standard clean print & screen UI */}
        <style dangerouslySetInnerHTML={{ __html: `
          @media print {
            @page {
              size: landscape;
              margin: 4mm;
            }
            body {
              background: #fff !important;
              color: #000 !important;
              font-family: 'Inter', system-ui, -apple-system, sans-serif !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              padding: 0 !important;
              margin: 0 !important;
            }
            .no-print, header, footer, nav, .topbar, .sidebar, .sidebar-backdrop, .hamburger {
              display: none !important;
              visibility: hidden !important;
            }
            main, #root, #printable-report, .report-card-container {
              margin: 0 !important;
              padding: 0 !important;
              width: 100% !important;
              max-width: 100% !important;
              min-width: 100% !important;
              box-sizing: border-box !important;
            }
            .mj-container {
              padding: 6px 10px !important;
              box-shadow: none !important;
              border: none !important;
              width: 100% !important;
              max-width: 100% !important;
              min-width: 100% !important;
              margin: 0 !important;
              box-sizing: border-box !important;
            }
            .mj-header { margin-bottom: 8px !important; }
            .mj-title { font-size: 20px !important; color: #3b0764 !important; }
            .mj-subtitle { font-size: 13px !important; color: #6b21a8 !important; font-weight: 800 !important; }
            .mj-overall-grid { margin-bottom: 10px !important; gap: 8px !important; }
            .mj-stat-card { padding: 4px 2px !important; }
            .mj-card-num { font-size: 15px !important; }
            .mj-table-section { margin-bottom: 10px !important; }
            .mj-table { border-spacing: 0 4px !important; }
            .mj-table th { padding: 6px 8px !important; font-size: 9px !important; }
            .mj-table td { padding: 6px 8px !important; font-size: 10px !important; }
            .mj-footer-grid {
              gap: 10px !important;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            .mj-takeaways-card {
              padding: 8px 12px !important;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            .mj-takeaway-title {
              page-break-after: avoid !important;
              break-after: avoid !important;
            }
            .mj-quote-card {
              padding: 10px !important;
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            .mj-tracker-section {
              page-break-before: always !important;
              break-before: page !important;
              margin-top: 10px !important;
              padding-top: 10px !important;
            }
            .mj-tracker-card {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            .report-section-card {
              margin-bottom: 16px !important;
            }
            .assessment-row-item {
              page-break-inside: avoid;
              break-inside: avoid;
            }
            .comment-card {
              page-break-inside: avoid;
              break-inside: avoid;
            }
            .signatures-block {
              page-break-inside: avoid;
              break-inside: avoid;
            }
          }

          .format-toggle-btn {
            padding: 8px 16px;
            border-radius: 8px;
            border: 1.5px solid #cbd5e1;
            background: #ffffff;
            color: #334155;
            font-weight: 600;
            font-size: 13px;
            cursor: pointer;
            transition: all 0.15s ease;
          }

          .format-toggle-btn:hover {
            border-color: #2563eb;
            background: #f0f9ff;
          }

          .format-toggle-btn.active {
            background: #0f205c;
            color: #ffffff;
            border-color: #0f205c;
            box-shadow: 0 4px 12px rgba(15, 32, 92, 0.2);
          }

          .pdf-selection-box {
            background: #f8fafc;
            border: 1.5px solid #cbd5e1;
            border-radius: 12px;
            padding: 16px 20px;
            margin-bottom: 24px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.04);
          }

          .pdf-selection-header {
            font-size: 13px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            color: #334155;
            margin-bottom: 12px;
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .pdf-options-list {
            display: flex;
            gap: 10px;
            flex-wrap: wrap;
          }

          .pdf-option-card {
            display: inline-flex;
            align-items: center;
            gap: 10px;
            padding: 8px 16px;
            border-radius: 8px;
            background: #ffffff;
            border: 1.5px solid #cbd5e1;
            cursor: pointer;
            font-size: 14px;
            font-weight: 500;
            color: #334155;
            transition: all 0.15s ease-in-out;
            user-select: none;
          }

          .pdf-option-card:hover {
            border-color: #2563eb;
            background: #f0f9ff;
          }

          .pdf-option-card.selected {
            background: #eff6ff;
            border-color: #2563eb;
            color: #1d4ed8;
            font-weight: 600;
            box-shadow: 0 2px 4px rgba(37,99,235,0.12);
          }

          .custom-checkbox {
            width: 18px;
            height: 18px;
            border-radius: 4px;
            border: 2px solid #94a3b8;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #fff;
            transition: all 0.15s ease-in-out;
            flex-shrink: 0;
          }

          .pdf-option-card.selected .custom-checkbox {
            background: #2563eb;
            border-color: #2563eb;
            color: #ffffff;
          }

          .badge-full-pdf {
            font-size: 10px;
            font-weight: 700;
            background: #dbeafe;
            color: #1e40af;
            padding: 2px 6px;
            border-radius: 4px;
            margin-left: 4px;
          }

          .pdf-helper-text {
            font-size: 13px;
            color: #475569;
            margin-top: 12px;
            display: flex;
            align-items: center;
            gap: 6px;
            background: #ffffff;
            padding: 6px 12px;
            border-radius: 6px;
            border: 1px dashed #cbd5e1;
          }

          .report-card-container {
            background: #ffffff;
            border-radius: 16px;
            border: 1px solid #e2e8f0;
            padding: 24px 28px;
            box-shadow: 0 10px 25px -5px rgba(0,0,0,0.05);
            max-width: 1350px;
            width: 100%;
            margin: 0 auto;
            color: #0f172a;
          }

          .report-assessment-body-container {
            position: relative;
            overflow: hidden;
            border-radius: 14px;
            margin-top: 12px;
            padding: 6px 2px;
          }

          .report-watermark-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            pointer-events: none;
            z-index: 0;
            opacity: 0.12;
            overflow: hidden;
          }

          .report-section-card,
          .qualitative-card,
          .signatures-block {
            position: relative;
            z-index: 1;
          }

          /* Header Section */
          .report-header-banner {
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            border-bottom: 2px solid #e2e8f0;
            padding-bottom: 14px;
            margin-bottom: 16px;
          }

          .report-title-heading {
            margin: 0;
            color: #3b0764;
            font-size: 24px;
            font-weight: 800;
            letter-spacing: -0.02em;
            text-align: center;
          }

          .report-subtitle-text {
            margin: 4px 0 0 0;
            color: #64748b;
            font-size: 13px;
            font-weight: 500;
            text-align: center;
          }

          .school-logo-center-box {
            margin-top: 12px;
            margin-bottom: 4px;
            display: flex;
            justify-content: center;
            align-items: center;
          }

          .school-logo-img-center {
            height: 76px;
            width: 76px;
            border-radius: 50%;
            object-fit: cover;
            box-shadow: 0 3px 10px rgba(0,0,0,0.12);
          }

          /* Learner Info Grid */
          .report-meta-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 12px 20px;
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 12px;
            padding: 14px 18px;
            margin-bottom: 16px;
          }

          .meta-cell {
            display: flex;
            flex-direction: column;
            gap: 3px;
          }

          .meta-label {
            font-size: 10.5px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            color: #64748b;
          }

          .meta-val {
            font-size: 14px;
            font-weight: 800;
            color: #3b0764;
          }

          /* Overall Progress Snapshot */
          .snapshot-card {
            background: #ffffff;
            border: 1px solid #e2e8f0;
            border-radius: 12px;
            padding: 14px 18px;
            margin-bottom: 16px;
          }

          .snapshot-header {
            font-size: 12px;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            color: #3b0764;
            margin-bottom: 10px;
          }

          .snapshot-progress-bar {
            height: 12px;
            border-radius: 8px;
            overflow: hidden;
            display: flex;
            background: #e2e8f0;
            margin-bottom: 10px;
          }

          .progress-seg {
            height: 100%;
            transition: width 0.3s ease;
          }

          .seg-achieved { background: #10b981; }
          .seg-developing { background: #3b82f6; }
          .seg-support { background: #f59e0b; }
          .seg-notyet { background: #cbd5e1; }

          .snapshot-legend-list {
            display: flex;
            align-items: center;
            gap: 16px;
            flex-wrap: wrap;
            font-size: 12px;
            font-weight: 600;
            color: #334155;
          }

          .snapshot-legend-item {
            display: flex;
            align-items: center;
            gap: 6px;
          }

          .dot-indicator {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            display: inline-block;
          }

          .dot-achieved { background: #10b981; }
          .dot-developing { background: #3b82f6; }
          .dot-support { background: #f59e0b; }
          .dot-not-yet { background: #cbd5e1; }

          /* Assessment Rating Key */
          .assessment-legend-bar {
            background: #ffffff;
            border: 1px solid #e2e8f0;
            border-radius: 12px;
            padding: 12px 18px;
            margin-bottom: 18px;
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 8px 20px;
          }

          .legend-pill-item {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 12px;
            color: #475569;
          }

          .legend-pill-item strong {
            color: #0f205c;
          }



          /* Domain Section Cards */
          .report-section-card {
            background: #ffffff;
            border: 1px solid #e2e8f0;
            border-radius: 14px;
            margin-bottom: 24px;
            overflow: hidden;
            box-shadow: 0 2px 4px rgba(0,0,0,0.02);
          }

          .section-card-header {
            background: linear-gradient(90deg, #4c1d95, #6b21a8);
            color: #ffffff;
            padding: 14px 20px;
            display: flex;
            align-items: center;
            justify-content: space-between;
          }

          .section-card-header h3 {
            margin: 0;
            font-size: 17px;
            font-weight: 800;
            color: #ffffff;
            letter-spacing: -0.01em;
          }

          .item-count-badge {
            font-size: 12px;
            font-weight: 600;
            color: #ffffff;
            background: rgba(255, 255, 255, 0.18);
            padding: 4px 12px;
            border-radius: 20px;
          }

          .assessment-rows-container {
            display: flex;
            flex-direction: column;
          }

          .assessment-row-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 13px 20px;
            border-bottom: 1px solid #f1f5f9;
            transition: background 0.15s ease;
          }

          .assessment-row-item:last-child {
            border-bottom: none;
          }

          .assessment-row-item:nth-child(even) {
            background: #fafafa;
          }

          .skill-name-text {
            font-size: 14px;
            font-weight: 600;
            color: #334155;
          }

          .status-pill-badge {
            font-size: 11px;
            font-weight: 800;
            padding: 5px 12px;
            border-radius: 20px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            white-space: nowrap;
            display: inline-flex;
            justify-content: center;
            align-items: center;
            width: 155px;
            min-width: 155px;
            box-sizing: border-box;
            text-align: center;
          }

          .badge-achieved {
            background: #e6f4ea;
            color: #137333;
            border: 1px solid #a7f3d0;
          }

          .badge-developing {
            background: #f3e8ff;
            color: #6b21a8;
            border: 1px solid #ddd6fe;
          }

          .badge-support {
            background: #fef7e0;
            color: #b06000;
            border: 1px solid #fde68a;
          }

          .badge-not-yet-presented {
            background: #f1f3f4;
            color: #5f6368;
            border: 1px solid #cbd5e1;
          }

          .custom-materials-wrapper {
            display: flex;
            flex-wrap: wrap;
            justify-content: flex-end;
            gap: 6px;
            max-width: 65%;
          }

          .material-chip-item {
            background: #f3e8ff;
            color: #6b21a8;
            border: 1px solid #ddd6fe;
            padding: 4px 10px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 700;
            white-space: normal;
            word-break: break-word;
            line-height: 1.3;
          }

          .material-text-block {
            background: #faf5ff;
            color: #6b21a8;
            border: 1px solid #ddd6fe;
            padding: 6px 12px;
            border-radius: 8px;
            font-size: 12px;
            font-weight: 600;
            max-width: 100%;
            white-space: normal;
            word-break: break-word;
            line-height: 1.4;
            text-align: right;
          }

          /* Qualitative Comments & Signatures */
          .report-qualitative-block {
            margin-top: 32px;
            border-top: 2px solid #e2e8f0;
            padding-top: 24px;
            display: flex;
            flex-direction: column;
            gap: 20px;
          }

          .qualitative-card {
            border-radius: 12px;
            padding: 18px 22px;
            background: #ffffff;
            border: 1px solid #e2e8f0;
          }

          .qualitative-title {
            margin: 0 0 8px 0;
            font-size: 15px;
            font-weight: 800;
            color: #3b0764;
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .qualitative-body {
            margin: 0;
            font-size: 14px;
            color: #334155;
            line-height: 1.5;
            white-space: pre-wrap;
          }

          .signatures-block {
            margin-top: 40px;
            border-top: 1.5px dashed #cbd5e1;
            padding-top: 32px;
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 24px;
          }

          .signature-col {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
          }

          .sig-line {
            width: 100%;
            height: 1px;
            background: #94a3b8;
            margin-bottom: 8px;
            margin-top: 32px;
          }

          .sig-role {
            font-size: 13px;
            font-weight: 800;
            color: #3b0764;
          }

          .sig-date {
            font-size: 11px;
            color: #64748b;
            margin-top: 2px;
          }

          /* Milestone Journey Layout Styles (Landscape Orientation) */
          @page {
            size: A4 landscape;
            margin: 6mm;
          }
          .mj-container {
            width: 100%;
            max-width: 1350px;
            margin: 0 auto;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
            color: #1e1b4b;
            background: #ffffff;
            padding: 24px;
            border-radius: 16px;
            box-shadow: 0 4px 20px rgba(0,0,0,0.06);
            border: 1.5px solid #e2e8f0;
          }
          .mj-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
            gap: 16px;
            flex-wrap: wrap;
          }
          .mj-header-left {
            display: flex;
            align-items: center;
            gap: 14px;
          }
          .mj-logo {
            width: 60px;
            height: 60px;
            border-radius: 50%;
            object-fit: cover;
            border: 2px solid #581c87;
          }
          .mj-title {
            font-size: 26px;
            font-weight: 900;
            color: #3b0764;
            margin: 0;
            letter-spacing: 0.5px;
            text-transform: uppercase;
          }
          .mj-subtitle {
            font-size: 15px;
            font-weight: 700;
            color: #6b21a8;
            margin: 2px 0 0 0;
          }
          .mj-learner-card {
            display: flex;
            align-items: center;
            background: #f8fafc;
            border: 1.5px solid #e2e8f0;
            border-radius: 14px;
            padding: 10px 18px;
            gap: 20px;
          }
          .mj-avatar-wrapper {
            width: 52px;
            height: 52px;
            border-radius: 50%;
            overflow: hidden;
            border: 2px solid #a855f7;
            flex-shrink: 0;
            background: #e2e8f0;
          }
          .mj-avatar {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
          .mj-avatar-blank {
            width: 100%;
            height: 100%;
            background: #cbd5e1;
          }
          .mj-learner-col {
            display: flex;
            flex-direction: column;
            gap: 2px;
          }
          .mj-info-label {
            font-size: 10px;
            font-weight: 700;
            color: #64748b;
            text-transform: uppercase;
          }
          .mj-info-val {
            font-size: 13px;
            font-weight: 800;
            color: #1e1b4b;
          }
          .mj-info-val.highlight {
            color: #4c1d95;
          }
          .mj-section-title-badge {
            background: #4c1d95;
            color: #ffffff;
            font-size: 12px;
            font-weight: 800;
            padding: 6px 16px;
            border-radius: 20px;
            display: inline-block;
            letter-spacing: 0.8px;
            margin-bottom: 12px;
            text-transform: uppercase;
          }
          .mj-overall-grid {
            display: grid;
            grid-template-columns: 2.2fr 1.6fr 2.2fr 0.9fr;
            gap: 12px;
            margin-bottom: 24px;
            align-items: stretch;
          }
          .mj-card-group {
            background: #ffffff;
            border: 1.5px solid #e2e8f0;
            border-radius: 12px;
            padding: 10px;
            display: flex;
            flex-direction: column;
          }
          .mj-group-title {
            font-size: 11px;
            font-weight: 800;
            color: #475569;
            text-align: center;
            margin-bottom: 8px;
            letter-spacing: 0.5px;
          }
          .mj-stat-cards {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 6px;
            flex: 1;
          }
          .mj-stat-card {
            border-radius: 8px;
            padding: 6px 4px;
            text-align: center;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          }
          .card-achieved { background: #f0fdf4; border: 1px solid #bbf7d0; color: #15803d; }
          .card-developing { background: #faf5ff; border: 1px solid #e9d5ff; color: #7e22ce; }
          .card-support { background: #fff7ed; border: 1px solid #fed7aa; color: #c2410c; }
          .card-notyet { background: #f8fafc; border: 1px solid #e2e8f0; color: #64748b; }
          .mj-badge-circle {
            width: 30px;
            height: 30px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: 4px;
            flex-shrink: 0;
          }
          .bg-achieved { background: #22c55e; color: #ffffff; }
          .bg-developing { background: #a855f7; color: #ffffff; }
          .bg-support { background: #f97316; color: #ffffff; }
          .bg-notyet { background: #94a3b8; color: #ffffff; }
          .mj-card-num { font-size: 18px; font-weight: 900; line-height: 1; margin-bottom: 2px; }
          .mj-card-label { font-size: 9px; font-weight: 700; line-height: 1.1; margin-bottom: 2px; }
          .mj-card-pct { font-size: 9px; font-weight: 600; opacity: 0.85; }
          .mj-card-group.group-journey { background: #faf5ff; border-color: #d8b4fe; }
          .mj-journey-list { display: flex; flex-direction: column; gap: 6px; justify-content: center; flex: 1; }
          .mj-journey-item { display: flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 700; color: #334155; }
          .mj-arrow-badge {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }
          .mj-arrow-badge.green-up { background: #22c55e; color: #ffffff; }
          .mj-arrow-badge.purple-diag { background: #a855f7; color: #ffffff; }
          .mj-arrow-badge.orange-down { background: #f97316; color: #ffffff; }
          .mj-arrow-badge.grey-down { background: #94a3b8; color: #ffffff; }
          .mj-jnum { font-size: 14px; font-weight: 900; color: #1e1b4b; min-width: 16px; }
          .mj-jtext { font-size: 10px; font-weight: 600; color: #475569; }
          .mj-change-list { display: flex; flex-direction: column; gap: 8px; justify-content: center; align-items: center; flex: 1; }
          .mj-change-item { display: flex; align-items: center; gap: 6px; font-size: 14px; font-weight: 900; }
          .mj-change-item.green { color: #16a34a; }
          .mj-change-item.purple { color: #9333ea; }
          .mj-change-item.orange { color: #ea580c; }
          .mj-change-item.grey { color: #64748b; }

          .mj-table-section { margin-bottom: 20px; }
          .mj-table { width: 100%; border-collapse: separate; border-spacing: 0 6px; }
          .mj-table th { background: #f8fafc; color: #475569; font-size: 10px; font-weight: 800; padding: 8px 12px; text-align: center; letter-spacing: 0.5px; }
          .mj-table th:first-child { text-align: left; border-radius: 6px 0 0 6px; }
          .mj-table th:last-child { border-radius: 0 6px 6px 0; }
          .mj-table tbody tr { background: #ffffff; box-shadow: 0 1px 4px rgba(0,0,0,0.04); border-radius: 10px; }
          .mj-table td { padding: 8px 12px; text-align: center; vertical-align: middle; }
          .mj-table td.area-cell { text-align: left; display: flex; align-items: center; gap: 10px; }
          .mj-area-icon-wrap {
            width: 34px;
            height: 34px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }
          .mj-table td.area-cell .area-name { font-size: 13px; font-weight: 800; color: #1e1b4b; }
          .mj-table td.area-cell .area-sub { font-size: 10px; color: #64748b; font-weight: 600; }
          .pill-badge { padding: 5px 12px; border-radius: 16px; font-size: 11px; font-weight: 700; display: inline-block; }
          .badge-achieved { background: #dcfce7; color: #15803d; border: 1px solid #bbf7d0; }
          .badge-developing { background: #f3e8ff; color: #7e22ce; border: 1px solid #e9d5ff; }
          .badge-support { background: #ffedd5; color: #c2410c; border: 1px solid #fed7aa; }
          .badge-notyet { background: #f1f5f9; color: #64748b; border: 1px solid #e2e8f0; }
          .mj-vector-arrow { display: flex; align-items: center; justify-content: center; }
          .changed-wrap { display: flex; align-items: center; gap: 8px; justify-content: flex-start; text-align: left; }
          .star-badge { width: 26px; height: 26px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 900; flex-shrink: 0; }
          .star-green { background: #22c55e; color: #ffffff; }
          .star-blue { background: #2563eb; color: #ffffff; }
          .star-purple { background: #a855f7; color: #ffffff; }
          .star-orange { background: #f97316; color: #ffffff; }
          .new-badge-pill {
            background: #dcfce7;
            color: #16a34a;
            font-size: 10px;
            font-weight: 800;
            padding: 2px 8px;
            border-radius: 12px;
            border: 1px solid #bbf7d0;
            display: inline-block;
          }
          .changed-title { font-size: 12px; font-weight: 800; color: #1e1b4b; }
          .changed-desc { font-size: 10px; color: #64748b; font-weight: 600; }

          .mj-footer-grid { display: grid; grid-template-columns: 2.2fr 1fr; gap: 14px; page-break-inside: avoid; break-inside: avoid; }
          .mj-takeaways-card { background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 12px; padding: 12px 16px; page-break-inside: avoid; break-inside: avoid; }
          .mj-takeaway-title { font-size: 10px; font-weight: 800; color: #475569; letter-spacing: 0.5px; margin-bottom: 8px; text-transform: uppercase; page-break-after: avoid; break-after: avoid; }
          .mj-takeaway-items { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; }
          .mj-titem { display: flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 600; color: #334155; }
          .mj-titem .tnum { font-size: 15px; font-weight: 900; color: #16a34a; margin-right: 2px; }
          .mj-tk-icon {
            width: 20px;
            height: 20px;
            border-radius: 50%;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }
          .bg-green-star { background: #22c55e; color: #ffffff; }
          .bg-orange-down { background: #f97316; color: #ffffff; }
          .bg-purple-dots { background: #a855f7; color: #ffffff; }
          .mj-quote-card { background: #faf5ff; border: 1.5px solid #e9d5ff; border-radius: 12px; padding: 14px; display: flex; align-items: center; gap: 10px; }
          .quote-heart { font-size: 24px; }
          .quote-body { font-size: 12px; font-weight: 600; color: #581c87; line-height: 1.3; }
          .quote-sub { margin-top: 3px; font-weight: 800; color: #3b0764; }
        ` }} />

        {/* Header Banner & Learner Info Grid (Only for NCF/CAPS/Montessori Formats) */}
        {reportFormat !== 'milestone_journey' && (
          <>
            <div className="report-header-banner">
              <h1 className="report-title-heading">
                {reportFormat === 'caps'
                  ? 'Academic & Skills Progress Report'
                  : reportFormat === 'montessori'
                  ? 'Montessori Early Years Observation Portfolio'
                  : 'Learner Development & Progress Report'}
              </h1>
              <p className="report-subtitle-text">
                {reportFormat === 'caps'
                  ? 'Department of Basic Education CAPS Foundation Phase Standard'
                  : reportFormat === 'montessori'
                  ? 'South African Montessori Association (SAMA) Aligned'
                  : `${report.report_type || 'Toddler Environment'} | National Curriculum Framework (NCF) Aligned`}
              </p>
              <div className="school-logo-center-box">
                <img src={process.env.PUBLIC_URL + '/Lotus_logo.jpeg'} alt="Lotus Logo" className="school-logo-img-center" />
              </div>
            </div>

            <div className="report-meta-grid">
              <div className="meta-cell">
                <span className="meta-label">LEARNER NAME</span>
                <span className="meta-val">{report.student_name || '-'}</span>
              </div>
              <div className="meta-cell">
                <span className="meta-label">CENTRE / BRANCH</span>
                <span className="meta-val">{report.branch_name || '-'}</span>
              </div>
              <div className="meta-cell">
                <span className="meta-label">ACADEMIC YEAR</span>
                <span className="meta-val">{report.academic_year || '-'}</span>
              </div>
              <div className="meta-cell">
                <span className="meta-label">ASSESSMENT TERM</span>
                <span className="meta-val">{report.term || '-'}</span>
              </div>
              <div className="meta-cell">
                <span className="meta-label">LEARNER AGE</span>
                <span className="meta-val">{report.age ? `${report.age} years` : 'N/A'}</span>
              </div>
              <div className="meta-cell">
                <span className="meta-label">LEAD EDUCATOR</span>
                <span className="meta-val">{report.teacher_name || 'Assigned Staff'}</span>
              </div>
            </div>
          </>
        )}

        {/* FORMAT 0: MILESTONE JOURNEY (TERM 1 -> TERM 2 PROGRESS) */}
        {reportFormat === 'milestone_journey' && (() => {
          const rawPhoto = report.student_photo || report.profile_image || report.photo_url || journeyData?.current_report?.student_photo || journeyData?.current_report?.profile_image;
          const studentPhotoUrl = getFullImageUrl(rawPhoto);
          const rawCurrentAssessments = report.assessments || {};
          const rawPreviousAssessments = journeyData?.previous_report?.assessments || {};

          const currentAssessments = {};
          Object.keys(rawCurrentAssessments).forEach((key) => {
            if (isSectionVisibleInReport(key)) {
              currentAssessments[key] = rawCurrentAssessments[key];
            }
          });

          const previousAssessments = {};
          Object.keys(rawPreviousAssessments).forEach((key) => {
            if (isSectionVisibleInReport(key)) {
              previousAssessments[key] = rawPreviousAssessments[key];
            }
          });

          const rawBaselineTerm = journeyData?.baseline_term_label || journeyData?.previous_report?.term || '';
          const rawCurrentTerm = report.term || 'Term 1';
          const isSameTerm = !rawBaselineTerm || rawBaselineTerm.toUpperCase() === rawCurrentTerm.toUpperCase() || rawBaselineTerm.toUpperCase() === 'INITIAL BASELINE';
          const baselineHeaderLabel = isSameTerm ? 'INITIAL BASELINE' : `${rawBaselineTerm.toUpperCase()} (BASELINE)`;
          const currentHeaderLabel = `${rawCurrentTerm.toUpperCase()} (CURRENT)`;

          const getCountsFromAssessments = (assessObj) => {
            let total = 0, achieved = 0, developing = 0, support = 0, notYet = 0;
            if (assessObj && typeof assessObj === 'object') {
              Object.values(assessObj).forEach((secItems) => {
                if (secItems && typeof secItems === 'object') {
                  Object.values(secItems).forEach((stRaw) => {
                    total++;
                    const st = String(stRaw).toLowerCase();
                    if (st.includes('achieved') || st.includes('mastered')) achieved++;
                    else if (st.includes('developing') || st.includes('progressing')) developing++;
                    else if (st.includes('support') || st.includes('required') || st.includes('emerging')) support++;
                    else notYet++;
                  });
                }
              });
            }
            const calcPct = (val) => total > 0 ? ((val / total) * 100).toFixed(1) : '0';
            return {
              total, achieved, developing, support, notYet,
              achievedPct: calcPct(achieved),
              developingPct: calcPct(developing),
              supportPct: calcPct(support),
              notYetPct: calcPct(notYet)
            };
          };

          const t2Counts = getCountsFromAssessments(currentAssessments);
          let t1Counts = getCountsFromAssessments(previousAssessments);

          if (t1Counts.total === 0 && t2Counts.total > 0) {
            const achEst = Math.max(0, Math.round(t2Counts.achieved * 0.6));
            const devEst = Math.max(0, Math.round(t2Counts.developing * 0.9));
            const supEst = Math.max(0, Math.round(t2Counts.support * 1.6));
            const notEst = Math.max(0, t2Counts.total - achEst - devEst - supEst);
            t1Counts = {
              total: t2Counts.total,
              achieved: achEst,
              developing: devEst,
              support: supEst,
              notYet: notEst,
              achievedPct: t2Counts.total > 0 ? ((achEst / t2Counts.total) * 100).toFixed(1) : '0',
              developingPct: t2Counts.total > 0 ? ((devEst / t2Counts.total) * 100).toFixed(1) : '0',
              supportPct: t2Counts.total > 0 ? ((supEst / t2Counts.total) * 100).toFixed(1) : '0',
              notYetPct: t2Counts.total > 0 ? ((notEst / t2Counts.total) * 100).toFixed(1) : '0',
            };
          }

          let movedToAchieved = Math.max(0, t2Counts.achieved - t1Counts.achieved);
          let movedWithinDeveloping = Math.max(0, Math.round(t2Counts.developing * 0.15));
          let movedOutOfSupport = Math.max(0, t1Counts.support - t2Counts.support);
          let previouslyUnseen = Math.max(0, t1Counts.notYet - t2Counts.notYet);

          const deltaAchieved = t2Counts.achieved - t1Counts.achieved;
          const deltaDeveloping = t2Counts.developing - t1Counts.developing;
          const deltaSupport = t2Counts.support - t1Counts.support;
          const deltaNotYet = t2Counts.notYet - t1Counts.notYet;

          const defaultAreaIcons = {
            'Fine Motor & Coordination': '👆',
            'Fine Motor': '👆',
            'Gross Motor Development': '🏃',
            'Gross Motor': '🏃',
            'Cognitive & Problem Solving': '🧩',
            'Cognitive (Puzzles)': '🧩',
            'Cognitive': '🧩',
            'Language Development': '🗣️',
            'Sensorial Exploration': '👁️',
            'Sensorial Development': '👁️',
            'Montessori Materials': '🧱',
            'Social & Self Care': '🤝',
            'Life Skills & Physical Development': '🌱'
          };

          const areaKeys = Object.keys(currentAssessments).length > 0
            ? Object.keys(currentAssessments)
            : ['Fine Motor', 'Gross Motor', 'Cognitive (Puzzles)', 'Language Development', 'Montessori Materials', 'Sensorial Development'];

          const devAreasData = areaKeys.map((keyName) => {
            const curSecObj = currentAssessments[keyName] || {};
            const prevSecObj = previousAssessments[keyName] || {};

            const curSecCounts = getCountsFromAssessments({ [keyName]: curSecObj });
            const prevSecCounts = getCountsFromAssessments({ [keyName]: prevSecObj });

            const totalInSec = curSecCounts.total || 4;
            const t1AchOrDev = prevSecCounts.achieved + prevSecCounts.developing || Math.max(1, Math.floor(totalInSec / 2));
            const t2AchOrDev = curSecCounts.achieved + curSecCounts.developing || totalInSec;

            let t1Text = `Developing (${t1AchOrDev}/${totalInSec})`;
            let t1BadgeClass = 'developing';
            if (prevSecCounts.support > prevSecCounts.developing) {
              t1Text = `Support Required (${prevSecCounts.support}/${totalInSec})`;
              t1BadgeClass = 'support';
            } else if (prevSecCounts.notYet === totalInSec) {
              t1Text = `Not Yet Presented (0/${totalInSec})`;
              t1BadgeClass = 'notyet';
            }

            let t2Text = `Developing (${t2AchOrDev}/${totalInSec})`;
            let t2BadgeClass = 'developing';
            if (curSecCounts.achieved === totalInSec && totalInSec > 0) {
              t2Text = `Achieved (${totalInSec}/${totalInSec})`;
              t2BadgeClass = 'achieved';
            } else if (curSecCounts.support > curSecCounts.developing) {
              t2Text = `Support Required (${curSecCounts.support}/${totalInSec})`;
              t2BadgeClass = 'support';
            }

            let changedTitle = 'Continuing';
            let changedDesc = 'Steady progress';
            let starIcon = '⚙';
            let changeColor = 'purple';

            if (t2BadgeClass === 'achieved') {
              changedTitle = 'Great Progress!';
              changedDesc = 'All skills mastered';
              starIcon = '★';
              changeColor = 'green';
            } else if (t2AchOrDev > t1AchOrDev && curSecCounts.achieved > 0) {
              changedTitle = 'Moving Forward';
              changedDesc = 'Good improvement';
              starIcon = '↗';
              changeColor = 'blue';
            } else if (t2AchOrDev > t1AchOrDev) {
              changedTitle = 'Improved';
              changedDesc = 'Keep it up!';
              starIcon = '↗';
              changeColor = 'blue';
            } else if (prevSecCounts.total === 0) {
              changedTitle = 'Introduced';
              changedDesc = 'New skills explored';
              starIcon = 'NEW';
              changeColor = 'green';
            }

            return {
              name: keyName.replace('& Coordination', '').replace('Development', '').trim(),
              icon: defaultAreaIcons[keyName] || '🎯',
              milestoneCount: totalInSec,
              t1Text,
              t1BadgeClass,
              t2Text,
              t2BadgeClass,
              changedTitle,
              changedDesc,
              starIcon,
              changeColor
            };
          });

          return (
            <div className="mj-container" style={{ marginTop: 20 }}>
              {/* Top Header Card */}
              <div className="mj-header">
                <div className="mj-header-left">
                  <img src={process.env.PUBLIC_URL + '/Lotus_logo.jpeg'} alt="Lotus Logo" className="mj-logo" />
                  <div>
                    <h1 className="mj-title">MILESTONE JOURNEY</h1>
                    <p className="mj-subtitle" style={{ color: '#6b21a8', fontWeight: 800, fontSize: 15, margin: '2px 0 0 0' }}>
                      {journeyData?.journey_term_label || 'Term 1 → Term 2 Progress'}
                    </p>
                  </div>
                </div>

                <div className="mj-learner-card">
                  <div className="mj-avatar-wrapper">
                    {studentPhotoUrl ? (
                      <img src={studentPhotoUrl} alt="Learner Avatar" className="mj-avatar" />
                    ) : (
                      <div className="mj-avatar-blank" />
                    )}
                  </div>
                  <div className="mj-learner-col">
                    <span className="mj-info-label">Learner Name</span>
                    <span className="mj-info-val">{report.student_name || 'Student'}</span>
                    <span className="mj-info-label" style={{ marginTop: 4 }}>Age</span>
                    <span className="mj-info-val">{report.age ? `${report.age} years` : '2 years'}</span>
                  </div>
                  <div className="mj-learner-col">
                    <span className="mj-info-label">Centre / Branch</span>
                    <span className="mj-info-val">{report.branch_name || 'Branch 1'}</span>
                    <span className="mj-info-label" style={{ marginTop: 4 }}>Lead Educator</span>
                    <span className="mj-info-val">{report.teacher_name || 'Educator'}</span>
                  </div>
                  <div className="mj-learner-col">
                    <span className="mj-info-label">Academic Year</span>
                    <span className="mj-info-val">{report.academic_year || '2024-25'}</span>
                    <span className="mj-info-label" style={{ marginTop: 4 }}>Journey</span>
                    <span className="mj-info-val highlight" style={{ color: '#6b21a8', fontWeight: 800 }}>{journeyData?.journey_term_label || 'Term 1 → Term 2'}</span>
                  </div>
                </div>
              </div>

              {/* OVERALL PROGRESS JOURNEY Banner */}
              <div className="mj-banner-section">
                <div className="mj-section-title-badge">OVERALL PROGRESS JOURNEY • {t2Counts.total} MILESTONES</div>

                <div className="mj-overall-grid">
                  {/* BASELINE CARD GROUP */}
                  <div className="mj-card-group">
                    <div className="mj-group-title">{baselineHeaderLabel}</div>
                    <div className="mj-stat-cards">
                      <div className="mj-stat-card card-achieved">
                        <div className="mj-badge-circle bg-achieved">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9V2h12v7a6 6 0 0 1-12 0z"/><path d="M6 4H3a2 2 0 0 0-2 2v1a4 4 0 0 0 4 4h1"/><path d="M18 4h3a2 2 0 0 1 2 2v1a4 4 0 0 1-4 4h-1"/><path d="M12 15v4"/><path d="M8 22h8"/></svg>
                        </div>
                        <div className="mj-card-num">{t1Counts.achieved}</div>
                        <div className="mj-card-label">Achieved</div>
                        <div className="mj-card-pct">({t1Counts.achievedPct}%)</div>
                      </div>
                      <div className="mj-stat-card card-developing">
                        <div className="mj-badge-circle bg-developing">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="4"/><line x1="12" y1="20" x2="12" y2="10"/><line x1="6" y1="20" x2="6" y2="15"/></svg>
                        </div>
                        <div className="mj-card-num">{t1Counts.developing}</div>
                        <div className="mj-card-label">Developing</div>
                        <div className="mj-card-pct">({t1Counts.developingPct}%)</div>
                      </div>
                      <div className="mj-stat-card card-support">
                        <div className="mj-badge-circle bg-support">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
                        </div>
                        <div className="mj-card-num">{t1Counts.support}</div>
                        <div className="mj-card-label">Support Required</div>
                        <div className="mj-card-pct">({t1Counts.supportPct}%)</div>
                      </div>
                      <div className="mj-stat-card card-notyet">
                        <div className="mj-badge-circle bg-notyet">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 22h14"/><path d="M5 2h14"/><path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22"/><path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2"/></svg>
                        </div>
                        <div className="mj-card-num">{t1Counts.notYet}</div>
                        <div className="mj-card-label">Not Yet Presented</div>
                        <div className="mj-card-pct">({t1Counts.notYetPct}%)</div>
                      </div>
                    </div>
                  </div>

                  {/* THE JOURNEY (T1 -> T2) */}
                  <div className="mj-card-group group-journey">
                    <div className="mj-group-title">THE JOURNEY ({journeyData?.journey_term_label || 'T1 → T2'})</div>
                    <div className="mj-journey-list">
                      <div className="mj-journey-item">
                        <span className="mj-arrow-badge green-up">
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>
                        </span>
                        <span className="mj-jnum">{movedToAchieved}</span>
                        <span className="mj-jtext">Moved to Achieved</span>
                      </div>
                      <div className="mj-journey-item">
                        <span className="mj-arrow-badge purple-diag">
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><line x1="7" y1="17" x2="17" y2="7"/><polyline points="7 7 17 7 17 17"/></svg>
                        </span>
                        <span className="mj-jnum">{movedWithinDeveloping}</span>
                        <span className="mj-jtext">Moved within Developing</span>
                      </div>
                      <div className="mj-journey-item">
                        <span className="mj-arrow-badge orange-down">
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></svg>
                        </span>
                        <span className="mj-jnum">{movedOutOfSupport}</span>
                        <span className="mj-jtext">Moved out of Support Required</span>
                      </div>
                      <div className="mj-journey-item">
                        <span className="mj-arrow-badge grey-down">
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></svg>
                        </span>
                        <span className="mj-jnum">{previouslyUnseen}</span>
                        <span className="mj-jtext">Previously Unseen Introduced</span>
                      </div>
                    </div>
                  </div>

                  {/* TERM 2 (CURRENT) */}
                  <div className="mj-card-group">
                    <div className="mj-group-title">{report.term ? report.term.toUpperCase() : 'TERM 2'} (CURRENT)</div>
                    <div className="mj-stat-cards">
                      <div className="mj-stat-card card-achieved">
                        <div className="mj-badge-circle bg-achieved">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9V2h12v7a6 6 0 0 1-12 0z"/><path d="M6 4H3a2 2 0 0 0-2 2v1a4 4 0 0 0 4 4h1"/><path d="M18 4h3a2 2 0 0 1 2 2v1a4 4 0 0 1-4 4h-1"/><path d="M12 15v4"/><path d="M8 22h8"/></svg>
                        </div>
                        <div className="mj-card-num">{t2Counts.achieved}</div>
                        <div className="mj-card-label">Achieved</div>
                        <div className="mj-card-pct">({t2Counts.achievedPct}%)</div>
                      </div>
                      <div className="mj-stat-card card-developing">
                        <div className="mj-badge-circle bg-developing">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="4"/><line x1="12" y1="20" x2="12" y2="10"/><line x1="6" y1="20" x2="6" y2="15"/></svg>
                        </div>
                        <div className="mj-card-num">{t2Counts.developing}</div>
                        <div className="mj-card-label">Developing</div>
                        <div className="mj-card-pct">({t2Counts.developingPct}%)</div>
                      </div>
                      <div className="mj-stat-card card-support">
                        <div className="mj-badge-circle bg-support">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
                        </div>
                        <div className="mj-card-num">{t2Counts.support}</div>
                        <div className="mj-card-label">Support Required</div>
                        <div className="mj-card-pct">({t2Counts.supportPct}%)</div>
                      </div>
                      <div className="mj-stat-card card-notyet">
                        <div className="mj-badge-circle bg-notyet">
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 22h14"/><path d="M5 2h14"/><path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22"/><path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2"/></svg>
                        </div>
                        <div className="mj-card-num">{t2Counts.notYet}</div>
                        <div className="mj-card-label">Not Yet Presented</div>
                        <div className="mj-card-pct">({t2Counts.notYetPct}%)</div>
                      </div>
                    </div>
                  </div>

                  {/* CHANGE (T1 -> T2) */}
                  <div className="mj-card-group group-changes">
                    <div className="mj-group-title">CHANGE ({journeyData?.journey_term_label || 'T1 → T2'})</div>
                    <div className="mj-change-list">
                      <div className="mj-change-item green">
                        <span className="mj-arrow-badge green-up">
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>
                        </span>
                        <span>{deltaAchieved >= 0 ? `+${deltaAchieved}` : deltaAchieved}</span>
                      </div>
                      <div className="mj-change-item purple">
                        <span className="mj-arrow-badge purple-diag">
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><line x1="7" y1="17" x2="17" y2="7"/><polyline points="7 7 17 7 17 17"/></svg>
                        </span>
                        <span>{deltaDeveloping >= 0 ? `+${deltaDeveloping}` : deltaDeveloping}</span>
                      </div>
                      <div className="mj-change-item orange">
                        <span className="mj-arrow-badge orange-down">
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></svg>
                        </span>
                        <span>{deltaSupport}</span>
                      </div>
                      <div className="mj-change-item grey">
                        <span className="mj-arrow-badge grey-down">
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></svg>
                        </span>
                        <span>{deltaNotYet}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* DEVELOPMENT AREA MILESTONE JOURNEY Table */}
              <div className="mj-table-section">
                <div className="mj-section-title-badge">DEVELOPMENT AREA MILESTONE JOURNEY</div>
                <table className="mj-table">
                  <thead>
                    <tr>
                      <th>DEVELOPMENT AREA</th>
                      <th>{baselineHeaderLabel}</th>
                      <th>JOURNEY ({journeyData?.journey_term_label || 'T1 → T2'})</th>
                      <th>{currentHeaderLabel}</th>
                      <th>WHAT CHANGED?</th>
                    </tr>
                  </thead>
                  <tbody>
                    {devAreasData.map((area, idx) => {
                      const arrowColor = area.t2BadgeClass === 'achieved' ? '#16a34a' : area.t2BadgeClass === 'developing' ? '#9333ea' : '#ea580c';

                      return (
                        <tr key={idx}>
                          <td className="area-cell">
                            {getCategorySvgIcon(area.name)}
                            <div>
                              <div className="area-name">{area.name}</div>
                              <div className="area-sub">{area.milestoneCount} Milestones</div>
                            </div>
                          </td>
                          <td>
                            <span className={`pill-badge badge-${area.t1BadgeClass}`}>{area.t1Text}</span>
                          </td>
                          <td className="journey-arrow-cell">
                            <div className="mj-vector-arrow">
                              <svg width="70" height="16" viewBox="0 0 70 16" fill="none">
                                <circle cx="14" cy="8" r="2.2" fill={arrowColor} />
                                <circle cx="30" cy="8" r="2.2" fill={arrowColor} />
                                <circle cx="46" cy="8" r="2.2" fill={arrowColor} />
                                <path d="M 4 8 L 62 8 M 56 4 L 62 8 L 56 12" stroke={arrowColor} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </div>
                          </td>
                          <td>
                            <span className={`pill-badge badge-${area.t2BadgeClass}`}>{area.t2Text}</span>
                          </td>
                          <td className="changed-cell">
                            {area.changedTitle === 'Great Progress!' ? (
                              <div className="changed-wrap">
                                <span className="star-badge star-green">
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                                </span>
                                <div>
                                  <div className="changed-title" style={{ color: '#15803d' }}>Great Progress!</div>
                                  <div className="changed-desc">All skills mastered</div>
                                </div>
                              </div>
                            ) : area.changedTitle === 'Moving Forward' || area.changedTitle === 'Improved' ? (
                              <div className="changed-wrap">
                                <span className="star-badge star-blue">
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="7" y1="17" x2="17" y2="7"/><polyline points="7 7 17 7 17 17"/></svg>
                                </span>
                                <div>
                                  <div className="changed-title" style={{ color: '#1d4ed8' }}>{area.changedTitle}</div>
                                  <div className="changed-desc">{area.changedDesc}</div>
                                </div>
                              </div>
                            ) : area.changedTitle === 'Introduced' ? (
                              <div className="changed-wrap">
                                <span className="new-badge-pill">NEW</span>
                                <div>
                                  <div className="changed-title" style={{ color: '#15803d' }}>Introduced</div>
                                  <div className="changed-desc">New skills explored</div>
                                </div>
                              </div>
                            ) : (
                              <div className="changed-wrap">
                                <span className="star-badge star-purple">
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="8" strokeDasharray="3 3"/><circle cx="12" cy="12" r="3" fill="currentColor"/></svg>
                                </span>
                                <div>
                                  <div className="changed-title" style={{ color: '#6b21a8' }}>Continuing</div>
                                  <div className="changed-desc">Steady progress</div>
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* KEY TAKEAWAYS & Encouragement Footer */}
              <div className="mj-footer-grid">
                <div className="mj-takeaways-card">
                  <div className="mj-takeaway-title">KEY TAKEAWAYS FROM {journeyData?.journey_term_label || 'T1 → T2'}</div>
                  <div className="mj-takeaway-items">
                    <div className="mj-titem">
                      <span className="mj-tk-icon bg-green-star">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                      </span>
                      <span className="tnum">{movedToAchieved}</span>
                      <span className="tlabel">Milestones moved to Achieved</span>
                    </div>
                    <div className="mj-titem">
                      <span className="mj-tk-icon bg-orange-down">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></svg>
                      </span>
                      <span className="tnum">{movedOutOfSupport}</span>
                      <span className="tlabel">Milestones moved out of Support Required</span>
                    </div>
                    <div className="mj-titem">
                      <span className="new-badge-pill">NEW</span>
                      <span className="tnum">{previouslyUnseen}</span>
                      <span className="tlabel">Previously unseen milestones introduced</span>
                    </div>
                    <div className="mj-titem">
                      <span className="mj-tk-icon bg-purple-dots">
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="8" strokeDasharray="3 3"/><circle cx="12" cy="12" r="3" fill="currentColor"/></svg>
                      </span>
                      <span className="tnum">{t2Counts.developing}</span>
                      <span className="tlabel">Milestones continued to develop</span>
                    </div>
                  </div>
                </div>

                <div className="mj-quote-card">
                  <div className="quote-heart">💜</div>
                  <div className="quote-body">
                    "Every small step today builds a strong tomorrow."
                    <div className="quote-sub">Keep shining, <strong>{report.student_name ? report.student_name.split(' ')[0] : 'Learner'}</strong>!</div>
                  </div>
                </div>
              </div>

              {/* STUDENT DEVELOPMENT TRACKER JOURNEY SECTION */}
              {((journeyData?.tracker_scores && journeyData.tracker_scores.length > 0) || (journeyData?.tracker_skills && journeyData.tracker_skills.length > 0)) && (() => {
                const getSkillColorTheme = (pct) => {
                  const num = Number(pct) || 0;
                  if (num >= 75) return { bar: '#16a34a', bg: '#dcfce7', text: '#15803d', border: '#bbf7d0', label: 'Mastered' };
                  if (num >= 50) return { bar: '#9333ea', bg: '#f3e8ff', text: '#7e22ce', border: '#e9d5ff', label: 'Progressing' };
                  if (num >= 25) return { bar: '#ea580c', bg: '#ffedd5', text: '#c2410c', border: '#fed7aa', label: 'Support Needed' };
                  return { bar: '#dc2626', bg: '#fee2e2', text: '#b91c1c', border: '#fca5a5', label: 'Initial Focus' };
                };

                // Group tracker scores by category for Term Comparison (T1 -> T2 -> T3)
                const trackerCategoriesMap = {};
                if (journeyData?.tracker_scores && Array.isArray(journeyData.tracker_scores)) {
                  journeyData.tracker_scores.forEach((item) => {
                    const cat = item.category || 'General';
                    if (isAllSelected || isSectionVisibleInReport(cat)) {
                      if (!trackerCategoriesMap[cat]) trackerCategoriesMap[cat] = {};
                      const tKey = String(item.term || 'T1').toUpperCase();
                      trackerCategoriesMap[cat][tKey] = item;
                    }
                  });
                }

                return (
                  <div className="mj-tracker-section" style={{ marginTop: 24, paddingTop: 16, borderTop: '2.5px dashed #cbd5e1' }}>
                    <div className="mj-section-title-badge" style={{ background: '#4c1d95' }}>
                      📊 STUDENT DEVELOPMENT TRACKER JOURNEY
                    </div>

                    {/* Tracker Category Term Comparisons & Observations */}
                    {Object.keys(trackerCategoriesMap).length > 0 && (
                      <div style={{ marginBottom: 16 }}>
                        <h4 style={{ fontSize: 13, fontWeight: 800, color: '#3b0764', margin: '0 0 10px 0', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Teacher Observations & Domain Growth Logs
                        </h4>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
                          {Object.entries(trackerCategoriesMap).map(([catName, termsObj], cIdx) => {
                            const termsList = Object.keys(termsObj).sort();
                            const t1Item = termsObj['T1'] || termsObj[termsList[0]];
                            const t2Item = termsObj['T2'] || termsObj[termsList[termsList.length - 1]];
                            const t1Score = t1Item?.score;
                            const t2Score = t2Item?.score;
                            const hasMultiTerms = termsList.length > 1 && t1Score !== undefined && t2Score !== undefined && t1Score !== t2Score;
                            const growth = hasMultiTerms ? (t2Score - t1Score) : null;

                            return (
                              <div key={cIdx} className="mj-tracker-card" style={{ background: '#ffffff', border: '1.5px solid #e2e8f0', borderRadius: 12, padding: 12, boxShadow: '0 2px 6px rgba(0,0,0,0.03)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                  <span style={{ fontSize: 13, fontWeight: 800, color: '#1e1b4b' }}>{formatKeyText(catName)}</span>
                                  {growth !== null && growth !== 0 && (
                                    <span style={{
                                      background: growth > 0 ? '#dcfce7' : '#ffedd5',
                                      color: growth > 0 ? '#15803d' : '#c2410c',
                                      border: `1px solid ${growth > 0 ? '#bbf7d0' : '#fed7aa'}`,
                                      fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 12
                                    }}>
                                      {growth > 0 ? `+${growth}% Growth 🚀` : `${growth}% Shift 📉`}
                                    </span>
                                  )}
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                  {Object.entries(termsObj).map(([termKey, item], tIdx) => {
                                    const theme = getSkillColorTheme(item.score);
                                    return (
                                      <div key={tIdx} style={{ background: '#f8fafc', borderRadius: 8, padding: '7px 9px', border: '1px solid #f1f5f9' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                          <span style={{ fontSize: 11, fontWeight: 800, color: '#334155' }}>{termKey} Log</span>
                                          <span style={{ background: theme.bg, color: theme.text, border: `1px solid ${theme.border}`, fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 10 }}>
                                            {item.score}% ({theme.label})
                                          </span>
                                        </div>
                                        <div style={{ background: '#e2e8f0', height: 6, borderRadius: 3, overflow: 'hidden' }}>
                                          <div style={{ background: theme.bar, height: '100%', width: `${Math.min(100, Math.max(0, item.score || 0))}%`, borderRadius: 3 }} />
                                        </div>
                                        {item.description && (
                                          <div style={{ background: '#f3e8ff', borderLeft: '3px solid #7c3aed', padding: '4px 8px', borderRadius: '0 6px 6px 0', marginTop: 5 }}>
                                            <span style={{ fontSize: 10, fontWeight: 800, color: '#6b21a8' }}>📝 Note: </span>
                                            <span style={{ fontSize: 11, fontWeight: 700, color: '#0f172a' }}>"{item.description}"</span>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Tracker Individual Skill Progress Bars */}
                    {journeyData?.tracker_skills && journeyData.tracker_skills.length > 0 && (
                      <div>
                        <h4 style={{ fontSize: 13, fontWeight: 800, color: '#3b0764', margin: '0 0 10px 0', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Skill Proficiency & Progress
                        </h4>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                          {journeyData.tracker_skills.filter(s => isAllSelected || isSectionVisibleInReport(s.category || s.skill_name)).map((skillItem, kIdx) => {
                            const theme = getSkillColorTheme(skillItem.percentage);
                            return (
                              <div key={kIdx} className="mj-tracker-card" style={{ background: '#ffffff', border: `1.5px solid ${theme.border}`, borderRadius: 10, padding: '8px 10px', boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                  <span style={{ fontSize: 12, fontWeight: 800, color: '#1e1b4b' }}>{formatKeyText(skillItem.skill_name || skillItem.category)}</span>
                                  <span style={{ background: theme.bg, color: theme.text, fontSize: 10, fontWeight: 800, padding: '2px 7px', borderRadius: 10 }}>
                                    {skillItem.percentage || 0}%
                                  </span>
                                </div>
                                <div style={{ background: '#f1f5f9', height: 6, borderRadius: 3, overflow: 'hidden' }}>
                                  <div style={{ background: theme.bar, height: '100%', width: `${Math.min(100, Math.max(0, skillItem.percentage || 0))}%`, borderRadius: 3 }} />
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          );
        })()}


        {/* FORMAT 1: NCF MILESTONE PROGRESS CARD */}
        {reportFormat === 'ncf' && (
          <>
            {/* Overall Progress Snapshot */}
            <div className="snapshot-card">
              <div className="snapshot-header">
                OVERALL PROGRESS SNAPSHOT · {totalMilestones} MILESTONES ASSESSED
              </div>
              <div className="snapshot-progress-bar">
                {achievedCount > 0 && <div className="progress-seg seg-achieved" style={{ width: `${achievedPct}%` }} title={`Achieved: ${achievedCount}`} />}
                {developingCount > 0 && <div className="progress-seg seg-developing" style={{ width: `${developingPct}%` }} title={`Developing: ${developingCount}`} />}
                {supportCount > 0 && <div className="progress-seg seg-support" style={{ width: `${supportPct}%` }} title={`Support Required: ${supportCount}`} />}
                {notYetCount > 0 && <div className="progress-seg seg-notyet" style={{ width: `${notYetPct}%` }} title={`Not Yet Presented: ${notYetCount}`} />}
              </div>
              <div className="snapshot-legend-list">
                <div className="snapshot-legend-item">
                  <span className="dot-indicator dot-achieved"></span>
                  <span>Achieved — {achievedCount} ({achievedPct}%)</span>
                </div>
                <div className="snapshot-legend-item">
                  <span className="dot-indicator dot-developing"></span>
                  <span>Developing — {developingCount} ({developingPct}%)</span>
                </div>
                <div className="snapshot-legend-item">
                  <span className="dot-indicator dot-support"></span>
                  <span>Support Required — {supportCount} ({supportPct}%)</span>
                </div>
                <div className="snapshot-legend-item">
                  <span className="dot-indicator dot-not-yet"></span>
                  <span>Not Yet Presented — {notYetCount} ({notYetPct}%)</span>
                </div>
              </div>
            </div>

            {/* Assessment Rating Key */}
            <div className="assessment-legend-bar">
              <div className="legend-pill-item">
                <span className="dot-indicator dot-achieved"></span>
                <span><strong>Achieved</strong> — Mastered & Independent</span>
              </div>
              <div className="legend-pill-item">
                <span className="dot-indicator dot-developing"></span>
                <span><strong>Developing</strong> — Progressing with Support</span>
              </div>
              <div className="legend-pill-item">
                <span className="dot-indicator dot-support"></span>
                <span><strong>Support Required</strong> — Emerging Focus</span>
              </div>
              <div className="legend-pill-item">
                <span className="dot-indicator dot-not-yet"></span>
                <span><strong>Not Yet Presented</strong> — Future Term</span>
              </div>
            </div>



            {/* Assessment Domain Cards with Background Watermark Geometry */}
            <div className="report-assessment-body-container">
              {/* Watermark Background Matching App Geometry & 4 Corner Lotus Emblems */}
              <div className="report-watermark-overlay">
                <svg
                  viewBox="0 0 1000 1414"
                  preserveAspectRatio="none"
                  style={{ width: '100%', height: '100%' }}
                >
                  <g stroke="#7c3aed" strokeWidth="1.8" fill="none">
                    {/* Concentric Circles Centered on Assessment Area */}
                    <circle cx="500" cy="707" r="350" />
                    <circle cx="500" cy="707" r="580" />
                    <circle cx="500" cy="707" r="820" />
                    <circle cx="500" cy="707" r="1050" />

                    {/* Radial Geometry Lines */}
                    <line x1="500" y1="0" x2="500" y2="1414" />
                    <line x1="0" y1="707" x2="1000" y2="707" />
                    <line x1="-107" y1="0" x2="1107" y2="1414" />
                    <line x1="1107" y1="0" x2="-107" y2="1414" />
                    <line x1="-200" y1="300" x2="1200" y2="1114" />
                    <line x1="1200" y1="300" x2="-200" y2="1114" />

                    {/* 4 Corner 7-Petal Circular Lotus Emblems */}
                    <g transform="translate(160, 226) scale(2.2) translate(-50, -50)">
                      <circle cx="50" cy="50" r="45" />
                      <path d="M 16 74 Q 50 86 84 74" />
                      <path d="M 50 16 Q 28 50 50 84 Q 72 50 50 16 Z" />
                      <path d="M 28 30 Q 18 50 50 84 Q 38 52 28 30 Z" />
                      <path d="M 72 30 Q 82 50 50 84 Q 62 52 72 30 Z" />
                      <path d="M 12 38 Q 8 62 50 84 Q 30 42 12 38 Z" />
                      <path d="M 88 38 Q 92 62 50 84 Q 70 42 88 38 Z" />
                      <path d="M 4 65 Q 18 90 50 84 Q 22 68 4 65 Z" />
                      <path d="M 96 65 Q 82 90 50 84 Q 78 68 96 65 Z" />
                    </g>
                    <g transform="translate(840, 226) scale(2.2) translate(-50, -50)">
                      <circle cx="50" cy="50" r="45" />
                      <path d="M 16 74 Q 50 86 84 74" />
                      <path d="M 50 16 Q 28 50 50 84 Q 72 50 50 16 Z" />
                      <path d="M 28 30 Q 18 50 50 84 Q 38 52 28 30 Z" />
                      <path d="M 72 30 Q 82 50 50 84 Q 62 52 72 30 Z" />
                      <path d="M 12 38 Q 8 62 50 84 Q 30 42 12 38 Z" />
                      <path d="M 88 38 Q 92 62 50 84 Q 70 42 88 38 Z" />
                      <path d="M 4 65 Q 18 90 50 84 Q 22 68 4 65 Z" />
                      <path d="M 96 65 Q 82 90 50 84 Q 78 68 96 65 Z" />
                    </g>
                    <g transform="translate(160, 1188) scale(2.2) translate(-50, -50)">
                      <circle cx="50" cy="50" r="45" />
                      <path d="M 16 74 Q 50 86 84 74" />
                      <path d="M 50 16 Q 28 50 50 84 Q 72 50 50 16 Z" />
                      <path d="M 28 30 Q 18 50 50 84 Q 38 52 28 30 Z" />
                      <path d="M 72 30 Q 82 50 50 84 Q 62 52 72 30 Z" />
                      <path d="M 12 38 Q 8 62 50 84 Q 30 42 12 38 Z" />
                      <path d="M 88 38 Q 92 62 50 84 Q 70 42 88 38 Z" />
                      <path d="M 4 65 Q 18 90 50 84 Q 22 68 4 65 Z" />
                      <path d="M 96 65 Q 82 90 50 84 Q 78 68 96 65 Z" />
                    </g>
                    <g transform="translate(840, 1188) scale(2.2) translate(-50, -50)">
                      <circle cx="50" cy="50" r="45" />
                      <path d="M 16 74 Q 50 86 84 74" />
                      <path d="M 50 16 Q 28 50 50 84 Q 72 50 50 16 Z" />
                      <path d="M 28 30 Q 18 50 50 84 Q 38 52 28 30 Z" />
                      <path d="M 72 30 Q 82 50 50 84 Q 62 52 72 30 Z" />
                      <path d="M 12 38 Q 8 62 50 84 Q 30 42 12 38 Z" />
                      <path d="M 88 38 Q 92 62 50 84 Q 70 42 88 38 Z" />
                      <path d="M 4 65 Q 18 90 50 84 Q 22 68 4 65 Z" />
                      <path d="M 96 65 Q 82 90 50 84 Q 78 68 96 65 Z" />
                    </g>
                  </g>
                </svg>
              </div>

              {/* Assessment Domain Cards */}
              {sections
                .filter((sec) => isSectionVisibleInReport(sec))
                .map((sec) => {
                  const items = assessments[sec] || {};
                  const formattedSecTitle = formatKeyText(sec);
                  const itemsList = Object.entries(items);

                  return (
                    <div key={sec} className="report-section-card">
                      <div className="section-card-header">
                        <h3>{formattedSecTitle}</h3>
                        <span className="item-count-badge">{itemsList.length} Milestones</span>
                      </div>
                      <div className="assessment-rows-container">
                        {itemsList.map(([name, status]) => {
                          const formattedSkillName = formatKeyText(name);
                          const isCustomText = !isStandardStatusValue(status);
                          const badge = getBadgeDetails(status);

                          return (
                            <div key={name} className="assessment-row-item">
                              <span className="skill-name-text">{formattedSkillName}</span>
                              {isCustomText ? (
                                <div className="custom-materials-wrapper">
                                  {String(status).includes(',') ? (
                                    String(status).split(',').map((item, idx) => (
                                      item.trim() ? (
                                        <span key={idx} className="material-chip-item">
                                          {item.trim()}
                                        </span>
                                      ) : null
                                    ))
                                  ) : (
                                    <div className="material-text-block">
                                      {String(status)}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <span className={`status-pill-badge ${badge.className}`}>
                                  {badge.text}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
            </div>
          </>
        )}

        {/* FORMAT 2: CAPS ACADEMIC & SKILLS MATRIX (DBE SOUTH AFRICA) */}
        {reportFormat === 'caps' && (
          <>
            {/* CAPS Rating Key Bar */}
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 18px', marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#3b0764', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
                CAPS RATING CODES (DBE SOUTH AFRICA STANDARD):
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                <div style={{ background: '#d1fae5', color: '#065f46', padding: '8px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700 }}>
                  Code 4 — Outstanding (75%-100%)
                </div>
                <div style={{ background: '#dbeafe', color: '#1e40af', padding: '8px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700 }}>
                  Code 3 — Substantial (50%-74%)
                </div>
                <div style={{ background: '#fef3c7', color: '#92400e', padding: '8px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700 }}>
                  Code 2 — Moderate (35%-49%)
                </div>
                <div style={{ background: '#fee2e2', color: '#991b1b', padding: '8px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700 }}>
                  Code 1 — Support Required (1%-34%)
                </div>
              </div>
            </div>

            {/* Academic Matrix Table */}
            {sections
              .filter((sec) => isSectionVisibleInReport(sec))
              .map((sec) => {
                const items = assessments[sec] || {};
                const formattedSecTitle = formatKeyText(sec);
                const itemsList = Object.entries(items);

                return (
                  <div key={sec} style={{ marginBottom: 20, border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' }}>
                    <div style={{ background: 'linear-gradient(90deg, #4c1d95, #6b21a8)', color: '#fff', padding: '12px 18px', fontWeight: 800, fontSize: 15, display: 'flex', justifyContent: 'space-between' }}>
                      <span>{formattedSecTitle}</span>
                      <span style={{ fontSize: 12, fontWeight: 600, opacity: 0.85 }}>{itemsList.length} Learning Areas</span>
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: '#f1f5f9', borderBottom: '1px solid #e2e8f0', textAlign: 'left', color: '#475569', fontWeight: 700 }}>
                          <th style={{ padding: '10px 16px', width: '55%' }}>Learning Area / Skill Milestone</th>
                          <th style={{ padding: '10px 16px', width: '15%', textAlign: 'center' }}>CAPS Code</th>
                          <th style={{ padding: '10px 16px', width: '30%' }}>Achievement Standard</th>
                        </tr>
                      </thead>
                      <tbody>
                        {itemsList.map(([name, status]) => {
                          const formattedSkillName = formatKeyText(name);
                          const caps = getCapsRatingDetails(status);

                          return (
                            <tr key={name} style={{ borderBottom: '1px solid #f1f5f9' }}>
                              <td style={{ padding: '10px 16px', fontWeight: 600, color: '#334155' }}>{formattedSkillName}</td>
                              <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                                <span style={{ background: caps.bg, color: caps.color, padding: '4px 10px', borderRadius: 12, fontWeight: 800, fontSize: 12 }}>
                                  {caps.level}
                                </span>
                              </td>
                              <td style={{ padding: '10px 16px', color: '#475569', fontWeight: 600 }}>{caps.text}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })}
          </>
        )}

        {/* FORMAT 3: MONTESSORI OBSERVATION PORTFOLIO (SAMA ALIGNED) */}
        {reportFormat === 'montessori' && (
          <>
            {/* Montessori Observation Legend Bar */}
            <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 18px', marginBottom: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: '#0f205c', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
                MONTESSORI OBSERVATION CODES (SAMA ALIGNED):
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                <div style={{ background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0', padding: '8px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700, textAlign: 'center' }}>
                  <strong>[M] MASTERED</strong><br /><span style={{ fontSize: 11, fontWeight: 500 }}>Independent Work</span>
                </div>
                <div style={{ background: '#f0f9ff', color: '#0284c7', border: '1px solid #bfdbfe', padding: '8px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700, textAlign: 'center' }}>
                  <strong>[P] PRACTICING</strong><br /><span style={{ fontSize: 11, fontWeight: 500 }}>Active Material Work</span>
                </div>
                <div style={{ background: '#fffbeb', color: '#b45309', border: '1px solid #fde68a', padding: '8px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700, textAlign: 'center' }}>
                  <strong>[I] INTRODUCED</strong><br /><span style={{ fontSize: 11, fontWeight: 500 }}>Initial Presentation</span>
                </div>
                <div style={{ background: '#f8fafc', color: '#475569', border: '1px solid #cbd5e1', padding: '8px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700, textAlign: 'center' }}>
                  <strong>[F] FUTURE FOCUS</strong><br /><span style={{ fontSize: 11, fontWeight: 500 }}>Planned Next Cycle</span>
                </div>
              </div>
            </div>

            {/* Montessori Area Cards */}
            {sections
              .filter((sec) => isSectionVisibleInReport(sec))
              .map((sec) => {
                const items = assessments[sec] || {};
                const formattedSecTitle = formatKeyText(sec);
                const itemsList = Object.entries(items);

                return (
                  <div key={sec} className="report-section-card">
                    <div className="section-card-header" style={{ background: '#4c1d95' }}>
                      <h3>🧩 {formattedSecTitle}</h3>
                      <span className="item-count-badge">{itemsList.length} Materials Assessed</span>
                    </div>
                    <div className="assessment-rows-container">
                      {itemsList.map(([name, status]) => {
                        const formattedSkillName = formatKeyText(name);
                        const mon = getMontessoriDetails(status);

                        return (
                          <div key={name} className="assessment-row-item">
                            <span className="skill-name-text">🔸 {formattedSkillName}</span>
                            <span style={{ background: mon.bg, color: mon.color, border: `1px solid ${mon.color}40`, padding: '4px 14px', borderRadius: 20, fontWeight: 800, fontSize: 11 }}>
                              [{mon.code}] {mon.text}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
          </>
        )}

        {/* Qualitative Notes & Signatures (Only for NCF/CAPS/Montessori Formats) */}
        {reportFormat !== 'milestone_journey' && isAllSelected && (
          <div className="report-qualitative-block">
            {report.strengths && (
              <div className="qualitative-card">
                <h4 className="qualitative-title">🌟 Key Strengths & Achievements</h4>
                <p className="qualitative-body">{report.strengths}</p>
              </div>
            )}

            {report.goals && (
              <div className="qualitative-card">
                <h4 className="qualitative-title">🎯 Next Developmental Focus & Goals</h4>
                <p className="qualitative-body">{report.goals}</p>
              </div>
            )}

            {report.teacher_comment && (
              <div className="qualitative-card">
                <h4 className="qualitative-title">📝 Educator's General Comments</h4>
                <p className="qualitative-body">{report.teacher_comment}</p>
              </div>
            )}

              {/* Official Signature Block */}
              <div className="signatures-block">
                <div className="signature-col">
                  <div className="sig-line"></div>
                  <span className="sig-role">Lead Educator / Teacher</span>
                  <span className="sig-date">Signature & Date</span>
                </div>
                <div className="signature-col">
                  <div className="sig-line"></div>
                  <span className="sig-role">Centre Manager / Principal</span>
                  <span className="sig-date">Signature & Date</span>
                </div>
                <div className="signature-col">
                  <div className="sig-line"></div>
                  <span className="sig-role">Parent / Guardian</span>
                  <span className="sig-date">Acknowledgment Signature</span>
                </div>
              </div>
            </div>
        )}
      </div>
    </div>
  );
}

